import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Star, RefreshCw, Info, Send } from "lucide-react";
import { Stars, monthLabel, wibMonth } from "@/pages/admin/AdminAppraisals";

const PINK = "#C4738A";
const MAROON = "#7C2D3E";
const inp = "h-10 w-full px-3.5 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-[#C4738A] focus:ring-2 focus:ring-[#C4738A]/10 transition-all disabled:bg-slate-50 disabled:text-slate-400";
const taCls = `${inp} h-auto py-2.5 resize-none`;
const lbl = "block text-xs font-semibold text-slate-700 mb-1.5";
const GRADE_CLS = { "Sangat Baik": "bg-emerald-50 text-emerald-600", "Baik": "bg-teal-50 text-teal-600", "Cukup": "bg-amber-50 text-amber-600", "Perlu Pembinaan": "bg-red-50 text-red-500" };
const REC_OPTIONS = [["tidak_ada", "Tidak Ada Usulan"], ["kenaikan_gaji", "Kenaikan Gaji"], ["perpanjangan_kontrak", "Perpanjangan Kontrak"], ["promosi", "Promosi"], ["pembinaan", "Pembinaan"]];

const grade = (s) => s == null ? null : s >= 4.5 ? "Sangat Baik" : s >= 3.5 ? "Baik" : s >= 2.5 ? "Cukup" : "Perlu Pembinaan";

function RateStars({ value, onChange, disabled, tid }) {
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" data-testid={`${tid}-${i}`} disabled={disabled} onClick={() => onChange(i)}
          className="disabled:cursor-default transition-transform hover:scale-110">
          <Star className={`w-5 h-5 ${i <= (value || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200 fill-slate-200"}`} />
        </button>
      ))}
    </span>
  );
}

export default function AdminAppraisalForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [aspects, setAspects] = useState([]);
  const [empDetail, setEmpDetail] = useState(null);
  const [autoInfo, setAutoInfo] = useState({});
  const [history, setHistory] = useState([]);
  const [locked, setLocked] = useState(false);
  const [meta, setMeta] = useState(null);
  const [saving, setSaving] = useState(null);
  const [form, setForm] = useState({
    employee_id: "", period: wibMonth(), scores: {},
    kelebihan: "", perlu_ditingkatkan: "", target_perbaikan: "",
    rec_type: "tidak_ada", rec_salary: "", rec_effective: "", rec_reason: "",
  });
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setScore = (aid, v) => setForm((s) => ({ ...s, scores: { ...s.scores, [aid]: v } }));

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active !== false && e.role !== "owner"))).catch(() => {});
    api.get("/admin/appraisal-aspects").then((r) => setAspects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/appraisals/${id}`).then((r) => {
      const a = r.data;
      setLocked(a.status === "diajukan");
      setMeta({ status: a.status, created_by: a.created_by, created_at: a.created_at, submitted_at: a.submitted_at });
      setAutoInfo(a.auto_details || {});
      setEmpDetail({ id: a.employee_id, name: a.employee_name, role: a.employee_role, photo: a.employee_photo, gaji: a.gaji_saat_ini });
      setForm({
        employee_id: a.employee_id, period: a.period, scores: a.scores || {},
        kelebihan: a.kelebihan || "", perlu_ditingkatkan: a.perlu_ditingkatkan || "", target_perbaikan: a.target_perbaikan || "",
        rec_type: a.rec_type || "tidak_ada", rec_salary: a.rec_salary || "", rec_effective: a.rec_effective || "", rec_reason: a.rec_reason || "",
      });
    }).catch(() => { toast.error("Penilaian tidak ditemukan"); nav("/admin/appraisals"); });
  }, [id]);

  const loadAuto = useCallback(async (empId, period) => {
    if (!empId || !period) return;
    try {
      const { data } = await api.get("/admin/appraisals/meta/attendance", { params: { employee_id: empId, period } });
      const autoAspect = aspects.find((a) => a.auto === "attendance");
      if (autoAspect) {
        setAutoInfo((s) => ({ ...s, [autoAspect.id]: data }));
        if (data.score) setScore(autoAspect.id, data.score);
      }
      const h = await api.get("/admin/appraisals/meta/history", { params: { employee_id: empId, period } });
      setHistory(h.data);
    } catch { /* biarkan manual */ }
  }, [aspects]);

  useEffect(() => {
    if (!form.employee_id || isEdit) return;
    api.get(`/admin/employees/${form.employee_id}`).then((r) => setEmpDetail({ ...r.data, gaji: (r.data.base_salary || 0) + (r.data.allowance || 0) })).catch(() => {});
  }, [form.employee_id]);

  useEffect(() => {
    if (form.employee_id && form.period && aspects.length && !isEdit) loadAuto(form.employee_id, form.period);
  }, [form.employee_id, form.period, aspects]);

  const scored = aspects.filter((a) => form.scores[a.id]);
  const totalW = scored.reduce((s, a) => s + a.weight, 0);
  const final = totalW ? Math.round(scored.reduce((s, a) => s + form.scores[a.id] * a.weight, 0) / totalW * 10) / 10 : null;
  const g = grade(final);
  const histAvg = history.length ? Math.round(history.reduce((s, h) => s + h.final_score, 0) / history.length * 10) / 10 : null;
  const chartRows = [...history.filter((h) => h.period !== form.period), ...(final != null ? [{ period: form.period, final_score: final, current: true }] : [])].slice(-4);
  const maxV = 5;

  const save = async (status) => {
    if (!form.employee_id) return toast.error("Pilih karyawan dulu");
    if (!form.period) return toast.error("Pilih periode penilaian");
    if (!scored.length) return toast.error("Beri nilai minimal 1 aspek");
    setSaving(status);
    const payload = { ...form, rec_salary: parseInt(form.rec_salary) || 0, status };
    try {
      if (isEdit) {
        await api.put(`/admin/appraisals/${id}`, payload);
        toast.success(status === "diajukan" ? "Penilaian diajukan ke Owner" : "Draft diperbarui");
      } else {
        await api.post("/admin/appraisals", payload);
        toast.success(status === "diajukan" ? "Penilaian diajukan ke Owner" : "Draft tersimpan");
      }
      nav("/admin/appraisals");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan penilaian"); }
    finally { setSaving(null); }
  };

  const empName = empDetail?.name || employees.find((e) => e.id === form.employee_id)?.name || "—";

  return (
    <div data-testid="appraisal-form" className="space-y-4 pb-10">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => nav("/admin/appraisals")} className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-[#C4738A] mb-1.5 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Karyawan / Penilaian</button>
          <h1 className="font-display text-2xl font-bold text-slate-900">{isEdit ? (locked ? "Detail Penilaian" : "Lanjutkan Penilaian") : "Penilaian Karyawan"}</h1>
          <p className="text-xs text-slate-400">Evaluasi kinerja dan pengembangan karier</p>
        </div>
        {!locked && (
          <div className="flex gap-2">
            <button data-testid="af-save-draft" onClick={() => save("draft")} disabled={!!saving} className="h-10 px-5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 transition-colors disabled:opacity-50">
              {saving === "draft" ? "Menyimpan..." : "Simpan Draft"}
            </button>
            <button data-testid="af-submit" onClick={() => save("diajukan")} disabled={!!saving} className="h-10 px-5 rounded-lg text-white text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50" style={{ backgroundColor: MAROON }}>
              <Send className="w-3.5 h-3.5" /> {saving === "diajukan" ? "Mengajukan..." : "Ajukan ke Owner"}
            </button>
          </div>
        )}
        {locked && <span className="text-[11px] font-bold bg-emerald-50 text-emerald-600 rounded-full px-3 py-1.5">Diajukan ke Owner{meta?.submitted_at ? ` • ${formatDateID(meta.submitted_at)}` : ""}</span>}
      </div>

      {/* Header karyawan */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-5 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-[220px]">
          <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden border border-slate-200 flex-shrink-0">
            {empDetail?.photo && <img src={empDetail.photo} alt="" className="w-full h-full object-cover" />}
          </div>
          {isEdit ? (
            <div><div className="text-sm font-bold text-slate-900">{empName}</div>
              <div className="text-[10px] text-slate-400 uppercase">{(empDetail?.role || "").replace(/_/g, " ")}</div></div>
          ) : (
            <div className="flex-1 max-w-xs">
              <select data-testid="af-employee" value={form.employee_id} onChange={(e) => setF("employee_id", e.target.value)} className={inp} disabled={locked}>
                <option value="">— Pilih karyawan —</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({(e.role || "").replace(/_/g, " ")})</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="border-l border-slate-100 pl-5">
          <div className="text-[10px] text-slate-400 mb-1">Periode Penilaian</div>
          {isEdit ? <div className="text-sm font-bold text-slate-900">{monthLabel(form.period)}</div>
            : <input data-testid="af-period" type="month" value={form.period} onChange={(e) => setF("period", e.target.value)} className={`${inp} w-44`} disabled={locked} />}
        </div>
        <div className="border-l border-slate-100 pl-5">
          <div className="text-[10px] text-slate-400 mb-1">Penilai</div>
          <div className="text-sm font-bold text-slate-900">{meta?.created_by || user?.name || "—"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">
        <div className="xl:col-span-2 space-y-5">
          {/* Aspek */}
          <section className="bg-white rounded-2xl border border-slate-100 p-6">
            <h2 className="font-display font-bold text-sm text-slate-900">Penilaian Kinerja</h2>
            <p className="text-[10px] text-slate-400 mb-4">Klik bintang untuk memberi nilai.</p>
            <table className="w-full">
              <thead><tr className="text-[10px] uppercase text-slate-400 border-b border-slate-100">
                <th className="text-left py-2 font-semibold">Aspek</th><th className="text-center py-2 font-semibold w-16">Bobot</th><th className="text-left py-2 font-semibold">Penilaian</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {aspects.map((a) => (
                  <tr key={a.id} data-testid={`af-aspect-${a.id}`}>
                    <td className="py-3 text-xs font-semibold text-slate-700">
                      {a.name}
                      {a.auto === "attendance" && <span className="ml-2 text-[9px] font-bold bg-emerald-50 text-emerald-600 rounded-full px-2 py-0.5">Otomatis</span>}
                      {autoInfo[a.id] && (
                        <span data-testid={`af-auto-note-${a.id}`} className="block text-[9px] text-slate-400 font-normal mt-0.5">
                          {autoInfo[a.id].note}
                          {!locked && autoInfo[a.id].score && (
                            <button onClick={() => loadAuto(form.employee_id, form.period)} className="ml-1.5 text-[#C4738A] font-semibold inline-flex items-center gap-0.5"><RefreshCw className="w-2.5 h-2.5" /> hitung ulang</button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-center text-xs text-slate-500">{a.weight}%</td>
                    <td className="py-3"><RateStars tid={`af-star-${a.id}`} value={form.scores[a.id] || 0} disabled={locked} onChange={(v) => setScore(a.id, v)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-[10px] text-slate-400 bg-slate-50 rounded-lg px-3 py-2 flex gap-3 flex-wrap">
              <span><b>1</b> Perlu pembinaan</span><span>•</span><span><b>3</b> Sesuai standar</span><span>•</span><span><b>5</b> Sangat baik</span>
            </div>
          </section>

          {/* Catatan evaluasi */}
          <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
            <h2 className="font-display font-bold text-sm text-slate-900">Catatan Evaluasi</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={lbl}>Kelebihan</label>
                <textarea data-testid="af-kelebihan" value={form.kelebihan} onChange={(e) => setF("kelebihan", e.target.value)} rows={3} disabled={locked} placeholder="Kelebihan karyawan periode ini..." className={taCls} /></div>
              <div><label className={lbl}>Perlu Ditingkatkan</label>
                <textarea data-testid="af-improve" value={form.perlu_ditingkatkan} onChange={(e) => setF("perlu_ditingkatkan", e.target.value)} rows={3} disabled={locked} placeholder="Hal yang perlu ditingkatkan..." className={taCls} /></div>
            </div>
            <div><label className={lbl}>Target Perbaikan</label>
              <textarea data-testid="af-target" value={form.target_perbaikan} onChange={(e) => setF("target_perbaikan", e.target.value)} rows={2} disabled={locked} placeholder="Target perbaikan periode berikutnya..." className={taCls} /></div>
          </section>
        </div>

        {/* Kolom kanan */}
        <div className="space-y-5 xl:sticky xl:top-4">
          <section className="bg-white rounded-2xl border border-slate-100 p-5">
            <h3 className="font-display font-bold text-sm text-slate-900 mb-3">Nilai Akhir</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <span data-testid="af-final-score" className="font-display text-4xl font-bold text-slate-900">{final != null ? String(final).replace(".", ",") : "—"}</span>
              <span className="text-sm text-slate-400">/ 5</span>
              {final != null && <Stars n={final} size="w-4 h-4" />}
              {g && <span data-testid="af-grade" className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${GRADE_CLS[g]}`}>{g}</span>}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Rata-rata {history.length} periode sebelumnya: <b className="text-slate-600">{histAvg != null ? `${String(histAvg).replace(".", ",")} / 5` : "belum ada"}</b></p>
            {chartRows.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] text-slate-400 mb-2">Riwayat Nilai</div>
                <div data-testid="af-history-chart" className="flex items-end gap-3 h-24">
                  {chartRows.map((h) => (
                    <div key={`${h.period}-${h.current ? "cur" : "past"}`} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-slate-700">{String(h.final_score).replace(".", ",")}</span>
                      <div className="w-full rounded-t-md" style={{ height: `${(h.final_score / maxV) * 100}%`, backgroundColor: h.current ? MAROON : "#E9D5DA" }} />
                      <span className="text-[9px] text-slate-400">{new Date(h.period + "-15").toLocaleDateString("id-ID", { month: "short", year: "2-digit" })}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="bg-white rounded-2xl border border-slate-100 p-5 space-y-3">
            <h3 className="font-display font-bold text-sm text-slate-900">Rekomendasi HR</h3>
            <div><label className={lbl}>Jenis Usulan</label>
              <select data-testid="af-rec-type" value={form.rec_type} onChange={(e) => setF("rec_type", e.target.value)} className={inp} disabled={locked}>
                {REC_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Gaji Saat Ini</label>
                <input data-testid="af-current-salary" value={empDetail ? formatIDR(empDetail.gaji || 0) : "—"} readOnly className={`${inp} bg-slate-50`} /></div>
              <div><label className={lbl}>Usulan Gaji</label>
                <input data-testid="af-rec-salary" type="number" min="0" value={form.rec_salary} onChange={(e) => setF("rec_salary", e.target.value)} placeholder="0" className={inp} disabled={locked || form.rec_type !== "kenaikan_gaji"} /></div>
            </div>
            <div><label className={lbl}>Tanggal Berlaku</label>
              <input data-testid="af-rec-effective" type="date" value={form.rec_effective} onChange={(e) => setF("rec_effective", e.target.value)} className={inp} disabled={locked} /></div>
            <div><label className={lbl}>Alasan Rekomendasi</label>
              <textarea data-testid="af-rec-reason" value={form.rec_reason} onChange={(e) => setF("rec_reason", e.target.value)} rows={3} placeholder="Alasan usulan..." className={taCls} disabled={locked} /></div>
            <p className="text-[10px] text-slate-400 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Keputusan akhir oleh owner. Perubahan gaji dilakukan manual lewat data karyawan.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
